import invariant from 'invariant';
import PropTypes from 'prop-types';

const cloneRect = ({left, top, right, bottom}) => ({left, top, right, bottom});
const choose = (...items) => items.find(x => x != null);
const getCorner = ({left, top, right, bottom}, corner = "") => {
  const x = (corner.includes("l") ? left : (corner.includes("r") ? right : (left + right) / 2));
  const y = (corner.includes("t") ? top : (corner.includes("b") ? bottom : (top + bottom) / 2));
  return {x, y};
}

/**
 * Align `elem` to `parent`, using alignment method(s). Assumes that the element is absolutely positioned. If more than one method is specified, will test against offset parent bounds.
 * @param {Element} elem DOM Element to align.
 * @param {object} alignRect Object with {left, top, right, bottom} parameters.
 * @param {object | object[]} method Object or a list of objects with `from` and `to` specifying which corners/sides to align. Corners are identified by a string containing one or two characters of L/T/R/B.
 * @param {number | number[]} [padding] Optional number or array specifying extra padding in pixels.
 */
function alignElement(elem, alignRect, method, padding) {
  if (!elem || !alignRect) return false; // do not cause errors on uninitialized refs
  const position = window.getComputedStyle(elem).position;
  invariant(position === "absolute" || position === "fixed", `Element passed to alignElement must have position: absolute or fixed, not ${position}`);

  const parent = (position === "absolute" ? elem.offsetParent : document.documentElement);
  if (!parent) return; // offsetParent is null only if the element is hidden
  
  const destBox = cloneRect(alignRect);
  if (typeof padding === "number") {
    destBox.top -= padding;
    destBox.right += padding;
    destBox.bottom += padding;
    destBox.left -= padding;
  } else if (Array.isArray(padding)) {
    destBox.top -= choose(padding[0], 0);
    destBox.right += choose(padding[1], padding[0], 0);
    destBox.bottom += choose(padding[2], padding[0], 0);
    destBox.left -= choose(padding[3], padding[1], padding[0], 0);
  }

  if (!elem.style.left) elem.style.left = "0px";
  if (!elem.style.top) elem.style.top = "0px";
  const sourceBox = cloneRect(elem.getBoundingClientRect());

  const tryAlign = (method, parentBox) => {
    const {from, to} = method;
    const ptFrom = getCorner(sourceBox, from.toLowerCase());
    const ptTo = getCorner(destBox, to.toLowerCase());
    const x = ptTo.x - ptFrom.x;
    const y = ptTo.y - ptFrom.y;
    const score = parentBox && Math.max(
      parentBox.left - sourceBox.left - x,
      sourceBox.right + x - parentBox.right,
      parentBox.top - sourceBox.top - y,
      sourceBox.bottom + y - parentBox.bottom
    );
    return {x, y, score, method};
  };

  let coords;
  if (!Array.isArray(method)) {
    coords = tryAlign(method);
  } else {
    let overflowParent = elem.parentElement;
    let parentStyle = window.getComputedStyle(overflowParent);
    while (overflowParent !== document.documentElement && parentStyle.overflow === "visible") {
      overflowParent = overflowParent.parentElement;
      parentStyle = window.getComputedStyle(overflowParent);
    }
    const parentBox = cloneRect(overflowParent.getBoundingClientRect());
    parentBox.left += (parseFloat(parentStyle.paddingLeft) || 0) + (parseFloat(parentStyle.borderLeftWidth) || 0);
    parentBox.top += (parseFloat(parentStyle.paddingTop) || 0) + (parseFloat(parentStyle.borderTopWidth) || 0);
    parentBox.right -= (parseFloat(parentStyle.paddingRight) || 0) + (parseFloat(parentStyle.borderRightWidth) || 0);
    parentBox.bottom -= (parseFloat(parentStyle.paddingBottom) || 0) + (parseFloat(parentStyle.borderBottomWidth) || 0);
    method.some(m => {
      const cur = tryAlign(m, parentBox);
      if (!coords || cur.score < coords.score) {
        coords = cur;
      }
      return cur.score <= 0;
    });
  }
  if (!coords) return false;

  const styles = {
    left: Math.round((parseFloat(elem.style.left) || 0) + coords.x) + "px",
    top: Math.round((parseFloat(elem.style.top) || 0) + coords.y) + "px",
  };
  if (coords.method.matchWidth) styles.width = Math.round(destBox.right - destBox.left) + "px";
  if (coords.method.matchHeight) styles.height = Math.round(destBox.bottom - destBox.top) + "px";
  return styles;
}

const cornerType = PropTypes.oneOf([
  "", "l", "L", "t", "T", "r", "R", "b", "B",
  "lt", "tl", "LT", "TL", "lT", "tL", "Lt", "Tl",
  "rt", "tr", "RT", "TR", "rT", "tR", "Rt", "Tr",
  "lb", "bl", "LB", "BL", "lB", "bL", "Lb", "Bl",
  "rb", "br", "RB", "BR", "rB", "bR", "Rb", "Br"
]);
const singleMethodType = PropTypes.shape({from: cornerType, to: cornerType, matchWidth: PropTypes.bool, matchHeight: PropTypes.bool, className: PropTypes.string});
alignElement.methodType = PropTypes.oneOfType([singleMethodType, PropTypes.arrayOf(singleMethodType)]);
alignElement.paddingType = PropTypes.oneOfType([PropTypes.number, PropTypes.arrayOf(PropTypes.number)]);

export default alignElement;